import { ReactNode } from "react";
import styled from "styled-components";
import { media } from "@literati/public-ui-shared-react";
import analytics from "analytics";
import { useField } from "formik";

import DLSErrorMessage from "components/DLS/DLSErrorMessage";
import GuidedSignUpCheckboxBoxy from "components/GuidedSignUp/GuidedSignUpCheckboxBoxy";
import GuidedSignUpCheckbox from "components/GuidedSignUp/GuidedSignUpCheckbox";
import GuidedSignUpCheckboxOther from "components/GuidedSignUp/GuidedSignUpCheckboxOther";

export enum GuidedSignUpMultiSelectVariant {
  default = "default",
  boxy = "boxy",
}

export type GuidedSignUpCheckboxGroupOptionKind = "default" | "other";

export type GuidedSignUpCheckboxGroupOption = {
  checkedBorderColor?: string;
  id: string;
  kind?: GuidedSignUpCheckboxGroupOptionKind;
  label: ReactNode;
  subLabel?: string;
  suppressCheckmarkIcon?: boolean;
  value: string;
};

export type GuidedSignUpCheckboxGroupOptions =
  GuidedSignUpCheckboxGroupOption[];

export interface GuidedSignUpCheckboxGroupProps {
  className?: string;
  /**
   * Formik field for free text when an option uses `kind: "other"`.
   * At most one option should use `kind: "other"`.
   */
  otherGoalTextFieldName?: string;
  options: GuidedSignUpCheckboxGroupOptions;
  name: string;
  onClick?: (option: GuidedSignUpCheckboxGroupOption) => void;
  /**
   * When true, the default (pill) variant shows the circular checkmark when selected.
   * Defaults to false (selected state uses background/border only).
   * Per-option `suppressCheckmarkIcon` overrides when set.
   */
  showSelectionCheckmark?: boolean;
  variant?: GuidedSignUpMultiSelectVariant;
}

const CheckboxGroup = styled.ul`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 0.875em;
  list-style: none;
  margin-left: 1rem;
  margin-right: 1rem;

  ${media.md} {
    margin-left: 0;
    margin-right: 0;
  }
`;

function GuidedSignUpCheckboxGroup({
  className = "",
  options,
  name,
  onClick,
  otherGoalTextFieldName,
  showSelectionCheckmark = false,
  variant = GuidedSignUpMultiSelectVariant.default,
}: GuidedSignUpCheckboxGroupProps) {
  const [_, meta] = useField<string[]>(name);
  const { error, touched } = meta;

  return (
    <CheckboxGroup className={className}>
      {options?.map((option) => (
        <li key={option.id}>
          {variant === GuidedSignUpMultiSelectVariant.boxy && (
            <GuidedSignUpCheckboxBoxy
              {...option}
              name={name}
              onClick={() => {
                if (typeof option.label === "string") {
                  analytics.trackAmplitudeClickEvent(option.label);
                }
                onClick?.(option);
              }}
            />
          )}
          {variant === GuidedSignUpMultiSelectVariant.default &&
            option.kind === "other" &&
            otherGoalTextFieldName && (
              <GuidedSignUpCheckboxOther
                checkedBorderColor={option.checkedBorderColor}
                id={option.id}
                label={option.label}
                name={name}
                otherTextFieldName={otherGoalTextFieldName}
                suppressCheckmarkIcon={
                  option.suppressCheckmarkIcon ?? !showSelectionCheckmark
                }
                value={option.value}
                onClick={() => {
                  if (typeof option.label === "string") {
                    analytics.trackAmplitudeClickEvent(option.label);
                  }
                  onClick?.(option);
                }}
              />
            )}
          {variant === GuidedSignUpMultiSelectVariant.default &&
            !(option.kind === "other" && otherGoalTextFieldName) && (
              <GuidedSignUpCheckbox
                checkedBorderColor={option.checkedBorderColor}
                id={option.id}
                label={option.label}
                name={name}
                suppressCheckmarkIcon={
                  option.suppressCheckmarkIcon ?? !showSelectionCheckmark
                }
                value={option.value}
                onClick={() => {
                  if (typeof option.label === "string") {
                    analytics.trackAmplitudeClickEvent(option.label);
                  }
                  onClick?.(option);
                }}
              />
            )}
        </li>
      ))}
      {touched && error && <DLSErrorMessage message={error} />}
    </CheckboxGroup>
  );
}

export default GuidedSignUpCheckboxGroup;
